import { useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { createPortal } from "react-dom";
import {
  COUNTRIES,
  PINNED_COUNTRY_CODES,
  NATIONAL_LENGTH_HINTS,
  detectCountryFromValue,
  flagEmoji,
  getCountry,
  DEFAULT_COUNTRY_ISO,
} from "../data/countries";
import type { Country } from "../data/countries";
import { IconChevronDown } from "./icons";

interface PhoneInputProps {
  /** Stored value, e.g. "+917602921397" (or "" / a bare national number). */
  value: string;
  /** Emits the stored value: "+" + dial code + national digits, or "" when empty. */
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
}

const maxNationalDigits = (country: Country) =>
  NATIONAL_LENGTH_HINTS[country.iso2] ?? Math.max(4, 15 - country.dial.length);

// The national digits from whatever's stored, with the selected country's
// dial code stripped off the front (e.g. dial "91" + "+917602921397" ->
// "7602921397"). Falls back to all digits if the prefix doesn't match yet
// (e.g. nothing typed, or legacy data stored without a country code).
const nationalDigits = (value: string, country: Country) => {
  const digits = (value ?? "").replace(/\D/g, "");
  return digits.startsWith(country.dial) ? digits.slice(country.dial.length) : digits;
};

// Phone input with a country code picker (flag + name + dial code), defaulting
// to India. Always emits the stored value as "+<dial><national digits>" (or ""
// when cleared) — the same external contract the fixed-+91 version used, so
// every existing call site keeps working unchanged.
export const PhoneInput = ({
  value,
  onChange,
  placeholder,
  disabled,
  required,
}: PhoneInputProps) => {
  const [country, setCountry] = useState<Country>(
    () => detectCountryFromValue(value) ?? getCountry(DEFAULT_COUNTRY_ISO),
  );
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const containerRef = useRef<HTMLSpanElement>(null);
  const ccButtonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Re-sync if `value` is swapped out from under us with a different dial
  // code (e.g. the parent form loads a different record to edit) — but never
  // fight the user's own typing/selection, and never reset just because the
  // field went blank.
  useEffect(() => {
    const digits = (value ?? "").replace(/\D/g, "");
    if (!digits || digits.startsWith(country.dial)) return;
    const match = detectCountryFromValue(digits);
    if (match) setCountry(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // The dropdown is portaled to <body> (see render below) so a scrollable
  // ancestor — e.g. a modal's overflow:auto body — can't clip or squeeze it.
  // That means position has to be computed in JS instead of plain CSS, and
  // "click outside" has to check both the trigger AND the portaled panel
  // (they're no longer DOM descendants of each other).
  const DROPDOWN_WIDTH = 272; // matches the 17rem in .phone-input__dropdown

  const reposition = () => {
    const rect = ccButtonRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Clamp so the panel never runs off the right (or left) edge of the
    // viewport — e.g. a "Phone" field in the right column of a 2-column
    // form inside a narrow modal.
    const left = Math.min(rect.left, window.innerWidth - DROPDOWN_WIDTH - 16);
    setCoords({ top: rect.bottom + 4, left: Math.max(16, left) });
  };

  useEffect(() => {
    if (!open) return;
    reposition();

    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    // capture:true so this still fires when the scroll happens inside a
    // scrollable ancestor (e.g. the modal body) rather than the window.
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const local = nationalDigits(value, country);

  const handle = (e: ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, maxNationalDigits(country));
    onChange(digits ? `+${country.dial}${digits}` : "");
  };

  const selectCountry = (next: Country) => {
    setCountry(next);
    setOpen(false);
    setQuery("");
    const digits = local.slice(0, maxNationalDigits(next));
    onChange(digits ? `+${next.dial}${digits}` : "");
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) => c.name.toLowerCase().includes(q) || c.dial.includes(q),
    );
  }, [query]);

  const pinned = useMemo(
    () =>
      query.trim()
        ? []
        : PINNED_COUNTRY_CODES.map((iso2) => getCountry(iso2)).filter(Boolean),
    [query],
  );

  const hint = NATIONAL_LENGTH_HINTS[country.iso2];
  const effectivePlaceholder = placeholder ?? (hint ? `${hint}-digit number` : "Phone number");

  return (
    <span className="phone-input" ref={containerRef}>
      <button
        ref={ccButtonRef}
        type="button"
        className="phone-input__cc"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="phone-input__flag" aria-hidden="true">
          {flagEmoji(country.iso2)}
        </span>
        <span>+{country.dial}</span>
        <IconChevronDown size={14} />
      </button>

      {open && coords
        ? createPortal(
            <div
              ref={dropdownRef}
              className="phone-input__dropdown"
              role="listbox"
              style={{ top: coords.top, left: coords.left }}
            >
              <input
                className="phone-input__search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search country or code…"
                autoFocus
              />
              <ul className="phone-input__list">
                {pinned.map((c) => (
                  <li key={c.iso2}>
                    <button type="button" onClick={() => selectCountry(c)}>
                      <span aria-hidden="true">{flagEmoji(c.iso2)}</span>
                      <span className="phone-input__list-name">{c.name}</span>
                      <span className="phone-input__list-dial">+{c.dial}</span>
                    </button>
                  </li>
                ))}
                {pinned.length ? <li className="phone-input__divider" /> : null}
                {filtered.length === 0 ? (
                  <li className="phone-input__empty">No matching country</li>
                ) : (
                  filtered.map((c) => (
                    <li key={c.iso2}>
                      <button type="button" onClick={() => selectCountry(c)}>
                        <span aria-hidden="true">{flagEmoji(c.iso2)}</span>
                        <span className="phone-input__list-name">{c.name}</span>
                        <span className="phone-input__list-dial">+{c.dial}</span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>,
            document.body,
          )
        : null}

      <input
        type="tel"
        inputMode="numeric"
        maxLength={maxNationalDigits(country)}
        value={local}
        onChange={handle}
        placeholder={effectivePlaceholder}
        disabled={disabled}
        required={required}
      />
    </span>
  );
};

export default PhoneInput;
